import { List } from 'lucide-react';
import React from 'react';

import type { TocData } from '../../../types/blog';

interface TocProps {
  data: TocData;
  /** Section heading — overrides the default "In this article" label */
  heading?: string;
}

/**
 * Renders the Table of Contents block.
 *
 * align='top'  (default) — renders inline in the content flow (max-w-sm card)
 * align='left' | 'right' — the inline card is still rendered here, but
 *   BlogPostPage extracts this section and places it in a sticky sidebar.
 *   The component itself doesn't need to know about its sidebar context.
 */
const Toc: React.FC<TocProps> = ({ data, heading }) => {
  const items = data.items ?? [];

  if (items.length === 0) return null;

  const label = heading?.trim() || 'In this article';

  return (
    <nav
      aria-label="Table of contents"
      className="rounded-xl vscode-bg-secondary border vscode-border p-5"
    >
      <div className="flex items-center gap-2 mb-4">
        <List className="w-4 h-4 text-cyan-400 shrink-0" />
        <p className="text-xs font-bold uppercase tracking-widest text-cyan-400">
          {label}
        </p>
      </div>
      <ol className="list-none m-0 p-0 space-y-0">
        {items.map((item, i) => (
          <li key={i} className="flex items-start gap-2.5 py-2 border-b vscode-border last:border-b-0">
            <span className="text-xs vscode-text-secondary shrink-0 tabular-nums w-4 text-right mt-0.5">
              {i + 1}.
            </span>
            <a
              href={`#toc-section-${i}`}
              className="text-sm vscode-text-primary leading-snug hover:text-[var(--vscode-primary)] transition-colors no-underline"
              onClick={(e) => {
                const target = document.getElementById(`toc-section-${i}`);
                if (target) {
                  e.preventDefault();
                  target.scrollIntoView({ behavior: 'smooth', block: 'start' });
                }
              }}
            >
              {item}
            </a>
          </li>
        ))}
      </ol>
    </nav>
  );
};

export default Toc;
